import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertCircle,
  Send,
  Copy,
  CheckCheck,
  Loader2,
  CircleCheck,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  REMINDER_TYPES,
  REMINDER_LABEL,
  REMINDER_TONE,
  buildReminderMessage,
  suggestReminderType,
  copyToClipboard,
  logReminderSent,
  markInvoicePaidFromReminder,
  formatUsd,
} from '@/lib/remindersData';
import { toast } from 'sonner';

const TONE_CLASSES = {
  default:
    'bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30',
  warning:
    'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
  danger:
    'bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30',
};

export const SendReminderDialog = ({
  open,
  onOpenChange,
  row,
  jobs = [],
  onLogged,
}) => {
  const { t } = useTranslation();
  const job = jobs.find((j) => j.id === row?.job_id) || null;
  const suggested = useMemo(
    () => suggestReminderType(row?.reminders_sent || []),
    [row?.reminders_sent]
  );
  const [type, setType] = useState(suggested);
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paidBusy, setPaidBusy] = useState(false);
  const [error, setError] = useState('');

  // Translation-aware reminder label resolver.
  const labelFor = (k) => t(`reminders.types.${k}`, { defaultValue: REMINDER_LABEL[k] || k });

  useEffect(() => {
    if (!open) return;
    setType(suggested);
    setError('');
    setCopied(false);
  }, [open, suggested]);

  // Rebuild message whenever type or row changes.
  useEffect(() => {
    if (!row) return;
    const msg = buildReminderMessage(type, {
      client: row.client,
      jobName: row.job_name,
      invoiceDesc: row.invoice_desc,
      amount: row.amount,
      due: row.due,
      daysPastDue: row.days_past_due,
    });
    setMessage(msg);
  }, [type, row]);

  if (!row) return null;

  const handleCopy = async () => {
    const ok = await copyToClipboard(message);
    if (ok) {
      setCopied(true);
      toast.success(t('reminders.dialog.copyToast'));
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error(t('reminders.dialog.copyFail'));
    }
  };

  const handleLog = async () => {
    if (!job) {
      setError(t('reminders.dialog.jobNotFound'));
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const updated = await logReminderSent(job, row.invoice_id, type);
      toast.success(t('reminders.dialog.loggedToast', { type: labelFor(type) }));
      onLogged?.(updated);
      onOpenChange?.(false);
    } catch (e) {
      setError(e?.message || t('reminders.dialog.logFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!job) return;
    setPaidBusy(true);
    try {
      const updated = await markInvoicePaidFromReminder(job, row.invoice_id);
      toast.success(t('reminders.dialog.markedPaidToast'));
      onLogged?.(updated);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.message || t('reminders.dialog.markPaidFail'));
    } finally {
      setPaidBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[hsl(var(--card))] border-[hsl(var(--hairline))] max-w-lg"
        data-testid="send-reminder-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-[hsl(var(--primary))]">
            <Send size={16} strokeWidth={2} />
            <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-muted-foreground">
              {t('reminders.dialog.eyebrow')}
            </span>
          </div>
          <DialogTitle className="text-foreground tracking-tight">
            {row.job_name}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {row.client} {'\u00b7'} {row.invoice_desc} {'\u00b7'} {formatUsd(row.amount)}
            {row.days_past_due > 0
              ? t('reminders.dialog.subtitlePastDue', { count: row.days_past_due })
              : row.due
              ? t('reminders.dialog.subtitleDue', { date: row.due })
              : ''}
          </DialogDescription>
        </DialogHeader>

        {/* Template picker */}
        <div className="space-y-2 mt-1">
          <div className="flex items-center gap-2">
            <Sparkles size={12} className="text-[hsl(var(--primary))]" />
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {t('reminders.dialog.suggested', {
                type: labelFor(suggested),
                count: (row.reminders_sent || []).length,
              })}
            </span>
          </div>
          <div
            className="grid grid-cols-3 gap-2"
            data-testid="reminder-type-grid"
          >
            {REMINDER_TYPES.map((key) => {
              const active = type === key;
              const tone = REMINDER_TONE[key];
              return (
                <button
                  type="button"
                  key={key}
                  onClick={() => setType(key)}
                  aria-pressed={active}
                  className={cn(
                    'rounded-md border px-3 py-2 text-xs font-medium uppercase tracking-[0.16em] transition-colors',
                    active
                      ? TONE_CLASSES[tone]
                      : 'border-border bg-[hsl(var(--muted))] text-muted-foreground hover:text-foreground hover:border-[hsl(var(--primary))]/30'
                  )}
                  data-testid={`reminder-type-${key}`}
                >
                  {labelFor(key)}
                  {key === suggested ? (
                    <Badge
                      variant="outline"
                      className="ml-2 uppercase text-[9px] tracking-[0.16em] border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10"
                    >
                      {t('reminders.dialog.pick')}
                    </Badge>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        {/* Message body */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {t('reminders.dialog.message')}
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleCopy}
              className="h-8 border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-[10px]"
              data-testid="reminder-copy"
            >
              {copied ? (
                <CheckCheck size={12} className="mr-1.5" />
              ) : (
                <Copy size={12} className="mr-1.5" />
              )}
              {copied ? t('reminders.dialog.copied') : t('reminders.dialog.copyToClipboard')}
            </Button>
          </div>
          <Textarea
            rows={9}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="bg-[hsl(var(--muted))] border-border text-foreground resize-none whitespace-pre-wrap text-sm leading-relaxed"
            data-testid="reminder-message"
          />
          <p className="text-[11px] text-muted-foreground">
            {t('reminders.dialog.editHint')}
          </p>
        </div>

        {error ? (
          <Alert
            className="border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
            data-testid="reminder-error"
          >
            <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
            <AlertDescription className="text-sm">{error}</AlertDescription>
          </Alert>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-3 pt-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            onClick={handleMarkPaid}
            disabled={submitting || paidBusy}
            className="mr-auto h-9 border-[hsl(var(--success))]/40 text-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/10 uppercase tracking-[0.16em] text-xs"
            data-testid="reminder-mark-paid"
          >
            {paidBusy ? (
              <Loader2 size={12} className="mr-2 animate-spin" />
            ) : (
              <CircleCheck size={12} className="mr-2" />
            )}
            {t('reminders.dialog.markPaid')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange?.(false)}
            disabled={submitting || paidBusy}
            className="text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]"
          >
            {t('reminders.dialog.close')}
          </Button>
          <Button
            type="button"
            onClick={handleLog}
            disabled={submitting || paidBusy}
            className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
            data-testid="reminder-log"
          >
            {submitting ? <Loader2 size={12} className="mr-2 animate-spin" /> : null}
            {t('reminders.dialog.logCta', { type: labelFor(type) })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendReminderDialog;
